import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTransactions, toggleDetailedPortfolio, clearState } from './Actions';
import {
  hasLoadedSelector,
  reportDataSelector,
  errorTypesSelector,
  errorsSelector,
  selectedPivotSelector,
  selectedFiltersPivotSelector,
  reportOptionsSelector,
  isDetailedShowingSelector,
} from './Selector';
import { isCurrentPlanSelectorReact } from '../../PlanSelectionNewWhatIfs/PlanSelectionNewWhatIfsSelector';
import { PortfolioDetails } from './components/PortfolioDetails';
import Toggle from '../../CommonComponents/Toggle';
import LoadingView from './components/Loading';
import { getPortfolioTitle } from './helpers';
import {
  DropdownMenu,
  DropdownMenuFirstItem,
  DropdownMenuSecondItem,
  DropdownMenuButton,
  HeaderContainer,
  Container,
  PortfolioDetailsContainer,
  ToggleContainer,
  LinkBlueButton,
  DropdownItemText,
} from './Styles';
import { useDocumentTitle } from '../../_hooks/useDocumentTitle';
import { isOptedIn } from '../../CommonUtils/isOptedIn';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { ERRORS_LIST } from './Consts';
import { Text } from '../../CommonComponents/OPL/Text/Text';

const { ADV } = window;

interface Props {
  clientId: string;
  fpId: string;
}

export const AssetAllocationReport: React.FC<Props> = props => {
  const { clientId, fpId } = props;
  const dispatch = useDispatch();

  useEffect(() => {
    if (clientId && fpId) {
      dispatch(fetchTransactions(clientId, fpId));
    }

    return function cleanUp() {
      dispatch(clearState());
    };
  }, [clientId, fpId, dispatch]);

  useDocumentTitle('Reports - Asset Allocation');

  const hasLoaded = useSelector(hasLoadedSelector);
  const reportData = useSelector(reportDataSelector);
  const errorTypes = useSelector(errorTypesSelector);
  const errors = useSelector(errorsSelector);
  const selectedPivot = useSelector(selectedPivotSelector);
  const selectedFiltersPivot = useSelector(selectedFiltersPivotSelector);
  const reportOptions = useSelector(reportOptionsSelector);
  const isCurrentPlan = useSelector(state => isCurrentPlanSelectorReact(state, props));
  const isDetailedShowing = useSelector(isDetailedShowingSelector);

  const hasAccessToEditFinancialPlan = isOptedIn('EditFinancialPlan');

  if (!hasLoaded) {
    return <LoadingView />;
  }

  if (errors.length > 0) {
    if (errorTypes !== null) {
      const message =
        errorTypes === ERRORS_LIST.missingAccounts ? 'You have not added any accounts.' : errors[0];

      return (
        <Container>
          <HeaderContainer>
            <Text as="h1" fontSize="h3" fontWeight={600} mb="16">
              Asset Allocation
            </Text>
          </HeaderContainer>
          <ErrorState
            height="100%"
            title="Some information is Missing"
            titleAs="h2"
            message={message}
            {...(hasAccessToEditFinancialPlan
              ? {
                  onAction: () => ADV.util.Router.go2('ClientEditPlan', { clientId }),
                  actionTitle: 'Edit Financial Plan',
                }
              : {})}
          />
        </Container>
      );
    }

    return (
      <Container>
        <HeaderContainer>
          <Text as="h1" fontSize="h3" fontWeight={600} mb="16">
            Asset Allocation
          </Text>
        </HeaderContainer>
        <ErrorState height="100%" titleAs="h2" message={errors[0]} />
      </Container>
    );
  }

  const { current, recommended } = reportData;
  const portfolioTitle = getPortfolioTitle(selectedPivot, selectedFiltersPivot[0]);

  return (
    <Container>
      <HeaderContainer>
        <Text as="h1" fontSize="h3" fontWeight={600} mb="16">
          Asset Allocation
        </Text>
        <DropdownMenu>
          {reportOptions.map(({ Pivot, Filters }) => {
            const pivotSelected = selectedPivot === Pivot;
            return (
              <DropdownMenuFirstItem key={Pivot} isSelected={pivotSelected}>
                {Pivot}
                <ul className="adv-dropdown">
                  {Filters.map((filter, index) => (
                    <DropdownMenuSecondItem
                      key={
                        // Necessary since accounts can have the same name.
                        /* eslint-disable-next-line react/no-array-index-key*/
                        `${filter}-${index}`
                      }
                      isSelected={selectedFiltersPivot[0] === filter && pivotSelected}
                    >
                      <DropdownMenuButton
                        onClick={() =>
                          dispatch(fetchTransactions(clientId, fpId, { Pivot, Filters: [filter] }))
                        }
                      >
                        <DropdownItemText>{filter}</DropdownItemText>
                      </DropdownMenuButton>
                    </DropdownMenuSecondItem>
                  ))}
                </ul>
              </DropdownMenuFirstItem>
            );
          })}
        </DropdownMenu>
        <ToggleContainer>
          <Toggle
            checked={isDetailedShowing}
            onChange={() => {
              dispatch(toggleDetailedPortfolio());
            }}
            className=""
            id="adv-details-toggle"
            isDisabled={false}
          />
          <p>Details</p>
        </ToggleContainer>
      </HeaderContainer>
      <PortfolioDetailsContainer>
        <PortfolioDetails
          assetValue={current.AssetValue}
          indicators={current.Indicators}
          series={current.Series}
          labels={current.Labels}
          planName="Current"
          portfolioName={portfolioTitle}
          isPrint={false}
          isTruncated={false}
        />
        {recommended.Series.length > 0 && !isCurrentPlan && (
          <PortfolioDetails
            assetValue={recommended.AssetValue}
            indicators={recommended.Indicators}
            series={recommended.Series}
            labels={recommended.Labels}
            planName="Recommended"
            portfolioName={portfolioTitle}
            isPrint={false}
            isTruncated={false}
          />
        )}
      </PortfolioDetailsContainer>

      <LinkBlueButton
        appendIcon="fa fa-external-link-alt"
        onClick={() => {
          ADV.util.Router.go2('Assumptions', {
            clientId,
          });
        }}
      >
        Definitions, Disclosures & Assumptions
      </LinkBlueButton>
    </Container>
  );
};
